import { isLeft } from 'fp-ts/Either';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useBeforeUnload, useNavigate } from 'react-router';
import type { Dapp } from '@src/dto';
import type { RightResult, RouterOutputs } from '../../../../types/TRPC';
import {
  getDappHomePath,
  getDappReleasePath,
  getDappReleasesPath,
} from '../../../../utils/dappRoutes';
import {
  getNewVersionBlockedMessage,
  getStoreSubmissionPermissionMessage,
} from '../../../../utils/publicationAccess';
import {
  submitPublicationUpdate,
  type PublicationProgressState,
} from '../../../../utils/publicationWorkflow';
import { pipe } from 'fp-ts/lib/function';
import { chainW, type TaskEither, tryCatch } from 'fp-ts/lib/TaskEither';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { MIN_PREVIEW_IMAGES } from '../../../../constants/form';
import {
  usePreviewFileHandler,
  useSingleFileHandler,
} from '../../../../hooks/useDappFileHandlers';
import {
  normalizeAvailableCountries,
  normalizeCountryCodeInputs,
} from '../../../../lib/countries';
import { useTRPC } from '../../../../hooks/useTRPC';
import { resolveToAndroidLocale } from '../../../../lib/android-languages';
import type { UpdateDappFormData } from '../../../../lib/schemas/formSchemas';
import type { TypedError } from '../../../../lib/typedError';
import type { DashboardPreviewData } from '../../../../types/dappForm';
import { getErrorMessage } from '../../../../utils/errorHandling';
import { captureAndReportError } from '../../../../utils/errorReporting';
import { uploadPortalMediaFile } from '../../../../utils/portalMediaUpload';
import {
  getMissingRequiredListingFields,
  type RequiredListingField,
} from '../../../../utils/storeListingCompleteness';
import type {
  StoreListingDraft,
  StoreListingDraftValues,
} from '../../../../utils/storeListingDraft';
import type { DashboardDetailsInputChangeEvent, FormState } from '../types';
import {
  applyTextInputLimit,
  createEmptyFormState,
  hasFormChanges,
} from '../utils/formState';
import {
  buildUpdateDappPayload,
  resolvePresignedUrl,
  resolvePresignedUrls,
} from '../utils/updatePayload';

interface UseDashboardDetailsFormOptions {
  dappId?: string;
}

/** Already-uploaded portal asset URLs shown as "existing" media in the form. */
export interface ListingAssetUrls {
  dappIconUrl: string | null;
  bannerUrl: string | null;
  dappPreviewUrls: (string | null)[];
  editorsChoiceGraphicUrl: string | null;
}

interface ListingBaseline {
  form: FormState;
  assets: ListingAssetUrls;
}

const JUST_SAVED_MS = 2000;

function emptyAssets(): ListingAssetUrls {
  return {
    dappIconUrl: null,
    bannerUrl: null,
    dappPreviewUrls: Array(MIN_PREVIEW_IMAGES).fill(null),
    editorsChoiceGraphicUrl: null,
  };
}

function padPreviewUrls(urls: (string | null)[]): (string | null)[] {
  const padded = [...urls];
  while (padded.length < MIN_PREVIEW_IMAGES) {
    padded.push(null);
  }
  return padded;
}

const compactUrls = (urls: (string | null)[]) =>
  urls.filter((url): url is string => Boolean(url));

function assetsEqual(a: ListingAssetUrls, b: ListingAssetUrls) {
  return (
    a.dappIconUrl === b.dappIconUrl &&
    a.bannerUrl === b.bannerUrl &&
    a.editorsChoiceGraphicUrl === b.editorsChoiceGraphicUrl &&
    JSON.stringify(compactUrls(a.dappPreviewUrls)) ===
      JSON.stringify(compactUrls(b.dappPreviewUrls))
  );
}

function setAt<T>(list: T[], index: number, value: T, fill: T): T[] {
  const next = [...list];
  while (next.length <= index) {
    next.push(fill);
  }
  next[index] = value;
  return next;
}

/** Text + asset values the server draft stores (files are uploaded first). */
function toDraftValues(
  form: FormState,
  assets: ListingAssetUrls,
): StoreListingDraftValues {
  return {
    dappName: form.dappName,
    subtitle: form.subtitle,
    description: form.description,
    languages: form.languages,
    availableCountriesMode: form.availableCountriesMode,
    availableCountries: form.availableCountries,
    appWebsite: form.appWebsite,
    contactEmail: form.contactEmail,
    supportEmail: form.supportEmail,
    licenseUrl: form.licenseUrl,
    privacyPolicyUrl: form.privacyPolicyUrl,
    editorsChoiceHeadline: form.editorsChoiceHeadline,
    editorsChoiceTag: form.editorsChoiceTag,
    releaseApprovalMode: form.releaseApprovalMode,
    dappIconUrl: assets.dappIconUrl,
    bannerUrl: assets.bannerUrl,
    dappPreviewUrls: compactUrls(assets.dappPreviewUrls),
    editorsChoiceGraphicUrl: assets.editorsChoiceGraphicUrl,
  };
}

function useFlash(durationMs: number): [boolean, () => void] {
  const [active, setActive] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return [
    active,
    () => {
      setActive(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setActive(false), durationMs);
    },
  ];
}

export function useDashboardDetailsForm({
  dappId,
}: UseDashboardDetailsFormOptions) {
  const trpc = useTRPC();
  const wallet = useWallet();
  const { visible: isWalletPickerOpen, setVisible: setWalletPickerOpen } =
    useWalletModal();
  const navigate = useNavigate();
  const [loadedDapp, setLoadedDapp] = useState<Dapp | null>(null);
  const [publicationState, setPublicationState] = useState<RightResult<
    RouterOutputs['release']['getStoreListingPublication']
  > | null>(null);
  const [publishInput, setPublishInput] = useState<UpdateDappFormData | null>(
    null,
  );
  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [publicationProgress, setPublicationProgress] =
    useState<PublicationProgressState | null>(null);
  const [publicationError, setPublicationError] = useState<string | null>(null);
  const [preparedReleaseId, setPreparedReleaseId] = useState<string | null>(
    null,
  );
  const publicationBusy = useRef(false);
  /** Server-side draft (`Dapp.storeListingDraft`); null once published/discarded. */
  const [draft, setDraft] = useState<StoreListingDraft | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [lastPublishedAt, setLastPublishedAt] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormState>(() =>
    createEmptyFormState(),
  );
  /** Server record — `buildUpdateDappPayload` diffs against it. */
  const [initialData, setInitialData] = useState<FormState | null>(null);
  const [serverAssets, setServerAssets] = useState<ListingAssetUrls>(() =>
    emptyAssets(),
  );
  /** Draft-hydrated state the "unsaved changes" indicators compare against. */
  const [baseline, setBaseline] = useState<ListingBaseline | null>(null);
  const [assets, setAssets] = useState<ListingAssetUrls>(() => emptyAssets());
  const [presignedUrls, setPresignedUrls] = useState<Record<string, string>>(
    {},
  );
  const [justSavedDraft, flashSavedDraft] = useFlash(JUST_SAVED_MS);
  useBeforeUnload((event) => {
    if (publicationBusy.current) event.preventDefault();
  });

  const remountForm = () => setFormKey((key) => key + 1);

  useEffect(() => {
    setFormData(createEmptyFormState());
    setInitialData(null);
    setBaseline(null);
    setDraft(null);
    setAssets(emptyAssets());
    setServerAssets(emptyAssets());
    setPresignedUrls({});
    setIsLoading(true);
    setIsSubmitting(false);
    setIsFormValid(false);
    setPublicationState(null);
    setPreparedReleaseId(null);
    setIsPublishOpen(false);
  }, [dappId]);

  useEffect(() => {
    const fetchDapp = async () => {
      if (!dappId) {
        captureAndReportError('No dapp ID provided');
        setIsLoading(false);
        return;
      }

      try {
        const result = await trpc.getDapp.query({ dappId });

        if (isLeft(result)) {
          captureAndReportError(result.left.message);
          toast.error('Failed to load dapp', {
            description: result.left.message,
          });
          setIsLoading(false);
          return;
        }

        const dapp = result.right;
        setLoadedDapp(dapp);
        setPresignedUrls(dapp.presignedUrls ?? {});
        setLastPublishedAt(dapp.storeListingPublishedAt ?? null);

        const resolvedLanguages = dapp.languages
          ? dapp.languages.map((lang) => resolveToAndroidLocale(lang) || lang)
          : ['en-US'];
        const availableCountriesMode =
          dapp.availableCountriesMode === 'exclude' ? 'exclude' : 'include';
        const resolvedAvailableCountries =
          availableCountriesMode === 'exclude'
            ? normalizeCountryCodeInputs(dapp.availableCountries)
            : normalizeAvailableCountries(dapp.availableCountries);
        const loadedAssets: ListingAssetUrls = {
          dappIconUrl: dapp.dappIconUrl || null,
          bannerUrl: dapp.bannerUrl || null,
          dappPreviewUrls: padPreviewUrls(dapp.dappPreviewUrls ?? []),
          editorsChoiceGraphicUrl: dapp.editorsChoiceGraphicUrl || null,
        };
        const loadedData: FormState = {
          dappName: dapp.dappName,
          packageName: dapp.packageName || '',
          subtitle: dapp.subtitle || '',
          description: dapp.description,
          dappIcon: null,
          banner: null,
          dappPreviews: Array(loadedAssets.dappPreviewUrls.length).fill(null),
          editorsChoiceHeadline: dapp.editorsChoiceHeadline || '',
          editorsChoiceTag: dapp.editorsChoiceTag || '',
          editorsChoiceGraphic: null,
          languages: resolvedLanguages,
          availableCountries: resolvedAvailableCountries,
          availableCountriesMode,
          appWebsite: dapp.appWebsite || '',
          contactEmail: dapp.contactEmail || '',
          supportEmail: dapp.supportEmail,
          tokenDistribution: dapp.tokenDistribution,
          walletAddress: dapp.walletAddress || '',
          licenseUrl: dapp.licenseUrl || '',
          privacyPolicyUrl: dapp.privacyPolicyUrl || '',
          releaseApprovalMode: dapp.releaseApprovalMode,
        };

        // A saved draft wins over the published record until it's published
        // or discarded.
        const draftValues = dapp.storeListingDraft?.values ?? {};
        const {
          dappIconUrl,
          bannerUrl,
          dappPreviewUrls,
          editorsChoiceGraphicUrl,
          ...draftText
        } = draftValues;
        const hydratedAssets: ListingAssetUrls = {
          dappIconUrl:
            dappIconUrl === undefined ? loadedAssets.dappIconUrl : dappIconUrl,
          bannerUrl:
            bannerUrl === undefined ? loadedAssets.bannerUrl : bannerUrl,
          dappPreviewUrls: dappPreviewUrls
            ? padPreviewUrls(dappPreviewUrls)
            : loadedAssets.dappPreviewUrls,
          editorsChoiceGraphicUrl:
            editorsChoiceGraphicUrl === undefined
              ? loadedAssets.editorsChoiceGraphicUrl
              : editorsChoiceGraphicUrl,
        };
        const hydratedData: FormState = {
          ...loadedData,
          ...Object.fromEntries(
            Object.entries(draftText).filter(
              ([, value]) => value !== undefined,
            ),
          ),
          dappPreviews: Array(hydratedAssets.dappPreviewUrls.length).fill(null),
        };

        setInitialData(loadedData);
        setServerAssets(loadedAssets);
        setFormData(hydratedData);
        setAssets(hydratedAssets);
        setBaseline({ form: hydratedData, assets: hydratedAssets });
        setDraft(dapp.storeListingDraft ?? null);
        remountForm();
        setIsLoading(false);
      } catch (error) {
        captureAndReportError('Failed to load dapp data:', error);
        toast.error('Failed to load dapp data');
        setIsLoading(false);
      }
    };

    void fetchDapp();
  }, [dappId, trpc]);

  useEffect(() => {
    if (!dappId) return;
    let cancelled = false;
    void trpc.release.getStoreListingPublication
      .query({ dappId })
      .then((result) => {
        if (!cancelled && !isLeft(result)) setPublicationState(result.right);
      })
      .catch(() => {
        /* Publish checks again before opening the dialog. */
      });
    return () => {
      cancelled = true;
    };
  }, [dappId, trpc]);

  const handleInputChange = (event: DashboardDetailsInputChangeEvent) => {
    const { name, value } = event.target;
    const type = 'type' in event.target ? event.target.type : undefined;

    if (
      (name === 'languages' || name === 'availableCountries') &&
      Array.isArray(value)
    ) {
      setFormData((previous) => ({
        ...previous,
        [name]: value as string[],
      }));
      return;
    }

    if (type === 'checkbox') {
      const { checked } = event.target as HTMLInputElement;
      setFormData((previous) => ({ ...previous, [name]: checked }));
      return;
    }

    setFormData((previous) => ({
      ...previous,
      [name]: applyTextInputLimit(name, value as string),
    }));
  };

  const resolvedPreviewUrls = resolvePresignedUrls(
    assets.dappPreviewUrls,
    presignedUrls,
  );

  const previewUploads = usePreviewFileHandler({
    formPreviews: formData.dappPreviews,
    existingUrls: resolvedPreviewUrls,
    onFileChange: (index, file) => {
      setFormData((previous) => ({
        ...previous,
        dappPreviews: setAt(previous.dappPreviews, index, file, null),
      }));
      setAssets((previous) => ({
        ...previous,
        dappPreviewUrls: setAt(
          previous.dappPreviewUrls,
          index,
          previous.dappPreviewUrls[index] ?? null,
          null,
        ),
      }));
    },
  });

  const handleSingleFileChange = useSingleFileHandler({
    onFileChange: (fieldName, file) => {
      setFormData((previous) => ({ ...previous, [fieldName]: file }));
    },
  });

  const resolvedDappIconUrl = resolvePresignedUrl(
    assets.dappIconUrl,
    presignedUrls,
  );
  const resolvedBannerUrl = resolvePresignedUrl(
    assets.bannerUrl,
    presignedUrls,
  );
  const resolvedEditorsChoiceGraphicUrl = resolvePresignedUrl(
    assets.editorsChoiceGraphicUrl,
    presignedUrls,
  );

  const previewPanelData: DashboardPreviewData = {
    dappName: formData.dappName,
    subtitle: formData.subtitle,
    description: formData.description,
    dappIcon: formData.dappIcon,
    banner: formData.banner,
    dappPreviews: formData.dappPreviews,
    tokenDistribution: formData.tokenDistribution,
    existingDappIconUrl: formData.dappIcon ? null : resolvedDappIconUrl,
    existingBannerUrl: formData.banner ? null : resolvedBannerUrl,
    existingPreviewUrls: formData.dappPreviews.map((file, index) =>
      file ? null : (resolvedPreviewUrls[index] ?? null),
    ),
    existingEditorsChoiceGraphicUrl: formData.editorsChoiceGraphic
      ? null
      : resolvedEditorsChoiceGraphicUrl,
  };

  const previewCount = formData.dappPreviews.filter(
    (file, index) => file || assets.dappPreviewUrls[index],
  ).length;
  const missingRequiredFields: RequiredListingField[] =
    getMissingRequiredListingFields({
      dappName: formData.dappName,
      subtitle: formData.subtitle,
      description: formData.description,
      hasIcon: Boolean(formData.dappIcon || assets.dappIconUrl),
      hasBanner: Boolean(formData.banner || assets.bannerUrl),
      previewCount,
      languages: formData.languages,
      appWebsite: formData.appWebsite,
      supportEmail: formData.supportEmail,
      licenseUrl: formData.licenseUrl,
      privacyPolicyUrl: formData.privacyPolicyUrl,
    });

  const hasChanges = baseline
    ? hasFormChanges(formData, baseline.form) ||
      !assetsEqual(assets, baseline.assets)
    : false;
  const hasUnpublishedChanges = initialData
    ? hasFormChanges(formData, initialData) ||
      !assetsEqual(assets, serverAssets)
    : false;

  const uploadFile = (
    file: File,
    folder: 'logos' | 'banners' | 'previews',
  ): Promise<string> =>
    uploadPortalMediaFile({
      trpc,
      file,
      folder,
      fileType:
        folder === 'logos'
          ? 'AppIcon'
          : folder === 'banners'
            ? 'ReleaseBanner'
            : 'ReleaseScreenshot',
      mode: folder === 'previews' ? 'media' : 'image',
    });

  /** Uploads pending files and merges them with the existing asset URLs. */
  const uploadPendingAssets = async (): Promise<ListingAssetUrls> => {
    const dappIconUrl = formData.dappIcon
      ? await uploadFile(formData.dappIcon, 'logos')
      : assets.dappIconUrl;
    const bannerUrl = formData.banner
      ? await uploadFile(formData.banner, 'banners')
      : assets.bannerUrl;
    const editorsChoiceGraphicUrl = formData.editorsChoiceGraphic
      ? await uploadPortalMediaFile({
          trpc,
          file: formData.editorsChoiceGraphic,
          folder: 'previews',
          fileType: 'ReleaseFeatureGraphic',
          mode: 'image',
        })
      : assets.editorsChoiceGraphicUrl;

    const dappPreviewUrls: string[] = [];
    for (let index = 0; index < formData.dappPreviews.length; index += 1) {
      const file = formData.dappPreviews[index];
      const url = file
        ? await uploadFile(file, 'previews')
        : assets.dappPreviewUrls[index];
      if (url) {
        dappPreviewUrls.push(url);
      }
    }

    return {
      dappIconUrl,
      bannerUrl,
      editorsChoiceGraphicUrl,
      dappPreviewUrls: padPreviewUrls(dappPreviewUrls),
    };
  };

  const clearPendingFiles = (form: FormState, previewCount: number) => ({
    ...form,
    dappIcon: null,
    banner: null,
    editorsChoiceGraphic: null,
    dappPreviews: Array(previewCount).fill(null),
  });

  const handleSaveDraft = async () => {
    if (!dappId || isSavingDraft || publicationBusy.current) {
      return;
    }

    setIsSavingDraft(true);
    try {
      const nextAssets = await uploadPendingAssets();
      const nextForm = clearPendingFiles(
        formData,
        nextAssets.dappPreviewUrls.length,
      );
      const result = await trpc.saveStoreListingDraft.mutate({
        dappId,
        values: toDraftValues(nextForm, nextAssets),
      });
      if (isLeft(result)) {
        throw new Error(result.left.message);
      }
      setDraft(result.right);
      // Sidebar badge / overview read the listDapps snapshot; refresh it.
      window.dispatchEvent(new CustomEvent('dappListUpdated'));
      setAssets(nextAssets);
      setFormData(nextForm);
      setBaseline({ form: nextForm, assets: nextAssets });
      remountForm();
      flashSavedDraft();
      return true;
    } catch (error) {
      captureAndReportError('Error saving listing draft:', error);
      toast.error('Failed to save draft', {
        description: getErrorMessage(error, 'An unexpected error occurred'),
      });
      return false;
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleDiscard = async () => {
    if (!dappId || !initialData || isSavingDraft || publicationBusy.current) {
      return;
    }
    setFormData(initialData);
    setAssets(serverAssets);
    setBaseline({ form: initialData, assets: serverAssets });
    remountForm();

    if (!draft) {
      return;
    }
    try {
      const result = await trpc.clearStoreListingDraft.mutate({ dappId });
      if (isLeft(result)) {
        throw new Error(result.left.message);
      }
      setDraft(null);
      window.dispatchEvent(new CustomEvent('dappListUpdated'));
    } catch (error) {
      captureAndReportError('Error discarding listing draft:', error);
      toast.error('Failed to discard draft', {
        description: getErrorMessage(error, 'An unexpected error occurred'),
      });
    }
  };

  const isInitialDraft =
    loadedDapp?.status === 'Draft' &&
    !loadedDapp.nftMintAddress &&
    !loadedDapp.importedFromNft &&
    loadedDapp.releaseCount === 0;

  const prepareListingUpdate = async (validatedData: UpdateDappFormData) => {
    const nextAssets = await uploadPendingAssets();
    const updateData = buildUpdateDappPayload({
      dappId: dappId!,
      initialData,
      validatedData,
    });

    if (
      nextAssets.dappIconUrl &&
      nextAssets.dappIconUrl !== serverAssets.dappIconUrl
    ) {
      updateData.dappIconUrl = nextAssets.dappIconUrl;
    }
    if (
      nextAssets.bannerUrl &&
      nextAssets.bannerUrl !== serverAssets.bannerUrl
    ) {
      updateData.bannerUrl = nextAssets.bannerUrl;
    }
    if (
      nextAssets.editorsChoiceGraphicUrl !==
      serverAssets.editorsChoiceGraphicUrl
    ) {
      // null clears the graphic server-side; the API input is nullable.
      updateData.editorsChoiceGraphicUrl =
        nextAssets.editorsChoiceGraphicUrl ?? null;
    }
    const nextPreviewUrls = compactUrls(nextAssets.dappPreviewUrls);
    if (
      nextPreviewUrls.length > 0 &&
      JSON.stringify(nextPreviewUrls) !==
        JSON.stringify(compactUrls(serverAssets.dappPreviewUrls))
    ) {
      updateData.dappPreviewUrls = nextPreviewUrls;
    }

    return { nextAssets, updateData };
  };

  const handlePublishConfirm = async () => {
    if (
      !dappId ||
      !publishInput ||
      !publicationState?.sourceRelease ||
      publicationState.reason ||
      publicationBusy.current
    )
      return;
    const walletMessage =
      getNewVersionBlockedMessage(loadedDapp) ||
      getStoreSubmissionPermissionMessage(loadedDapp);
    if (
      walletMessage ||
      !wallet.publicKey ||
      wallet.publicKey.toBase58() !== loadedDapp?.walletAddress ||
      !wallet.signTransaction
    )
      return;
    publicationBusy.current = true;
    setIsSubmitting(true);
    setPublicationError(null);
    const validatedData = publishInput;
    let releaseId: string | null = null;
    try {
      const { nextAssets, updateData } =
        await prepareListingUpdate(validatedData);

      const result = await trpc.release.prepareStoreListingPublication.mutate({
        listing: updateData,
        sourceReleaseId: publicationState.sourceRelease.id,
        idempotencyKey: crypto.randomUUID(),
      });
      if (isLeft(result)) throw new Error(result.left.message);
      releaseId = result.right.releaseId;
      setPreparedReleaseId(releaseId);
      const savedForm = clearPendingFiles(
        formData,
        nextAssets.dappPreviewUrls.length,
      );
      setDraft(null);
      setInitialData(savedForm);
      setServerAssets(nextAssets);
      setFormData(savedForm);
      setAssets(nextAssets);
      setBaseline({ form: savedForm, assets: nextAssets });
      remountForm();
      window.dispatchEvent(new CustomEvent('dappListUpdated'));
      const submitted = await submitPublicationUpdate({
        trpc,
        wallet,
        dappId,
        source: {
          kind: 'existingRelease',
          sourceReleaseId: publicationState.sourceRelease.id,
        },
        whatsNew: 'Store listing updated.',
        ingestionSessionId: result.right.id,
        onProgress: setPublicationProgress,
      });
      toast.success('Listing update submitted for review');
      window.dispatchEvent(new CustomEvent('dappListUpdated'));
      navigate(
        getDappReleasePath(
          { id: dappId, packageName: formData.packageName },
          submitted.releaseId,
        ),
      );
    } catch (error) {
      captureAndReportError('Error publishing listing:', error);
      if (releaseId) {
        setPublicationError(
          getErrorMessage(error, 'Unable to finish publication.'),
        );
      } else {
        // A timed-out response may still have created a release. Recheck before allowing another attempt.
        setIsPublishOpen(false);
        toast.error('Could not start listing publication', {
          description: getErrorMessage(error),
        });
      }
    } finally {
      publicationBusy.current = false;
      setIsSubmitting(false);
    }
  };

  const handleSubmit = (
    input: TaskEither<TypedError<'InvalidFormData'>, UpdateDappFormData>,
  ): TaskEither<unknown, unknown> =>
    pipe(
      input,
      chainW((validatedData) =>
        tryCatch(
          async () => {
            if (!dappId || publicationBusy.current || isSavingDraft) return;
            publicationBusy.current = true;
            setIsSubmitting(true);
            try {
              if (isInitialDraft) {
                const appWallet =
                  loadedDapp.walletAddress || wallet.publicKey?.toBase58();
                if (!appWallet)
                  throw new Error(
                    'Connect your app wallet to finish setup. You can still save a draft.',
                  );
                const { updateData } =
                  await prepareListingUpdate(validatedData);
                updateData.walletAddress = appWallet;
                updateData.completeDraft = true;
                const saved = await trpc.updateDapp.mutate(updateData);
                if (isLeft(saved)) throw new Error(saved.left.message);
                toast.success('Listing saved', {
                  description: 'Your app is ready for the next step.',
                });
                window.dispatchEvent(new CustomEvent('dappListUpdated'));
                navigate(getDappHomePath(saved.right));
                return;
              }
              const result =
                await trpc.release.getStoreListingPublication.query({ dappId });
              if (isLeft(result)) throw new Error(result.left.message);
              setPublicationState(result.right);
              setPublishInput(validatedData);
              setPublicationProgress(null);
              setPublicationError(null);
              setPreparedReleaseId(null);
              setIsPublishOpen(true);
            } catch (error) {
              toast.error(
                isInitialDraft
                  ? 'Could not save listing'
                  : 'Could not check release status',
                {
                  description: getErrorMessage(error),
                },
              );
              throw error;
            } finally {
              publicationBusy.current = false;
              setIsSubmitting(false);
            }
          },
          (error) => error,
        ),
      ),
    );

  const releaseToView =
    preparedReleaseId || publicationState?.blockingRelease?.id;
  const publicationReleasePath = dappId
    ? releaseToView
      ? getDappReleasePath(
          { id: dappId, packageName: formData.packageName },
          releaseToView,
        )
      : getDappReleasesPath({ id: dappId, packageName: formData.packageName })
    : '';
  const handleViewRelease = async () => {
    if (hasChanges && !(await handleSaveDraft())) return;
    navigate(publicationReleasePath);
  };

  const updateAsset = (patch: Partial<ListingAssetUrls>) =>
    setAssets((previous) => ({ ...previous, ...patch }));

  return {
    isLoading,
    isInitialDraft,
    needsAppWallet: isInitialDraft && !loadedDapp.walletAddress,
    isSubmitting,
    isSavingDraft,
    isFormValid,
    hasChanges,
    hasUnpublishedChanges,
    hasDraft: draft !== null,
    justSavedDraft,
    justPublished: false,
    publicationState,
    isPublishOpen: isPublishOpen && !isWalletPickerOpen,
    openWalletPicker: () => setWalletPickerOpen(true),
    publicationProgress,
    publicationError,
    handlePublishConfirm,
    closePublishDialog: () => setIsPublishOpen(false),
    handleViewRelease,
    hasUnfinishedRelease: Boolean(
      preparedReleaseId || publicationState?.reason === 'unfinished',
    ),
    walletReady: Boolean(
      wallet.publicKey &&
      wallet.publicKey.toBase58() === loadedDapp?.walletAddress &&
      wallet.signTransaction &&
      !getNewVersionBlockedMessage(loadedDapp) &&
      !getStoreSubmissionPermissionMessage(loadedDapp),
    ),
    walletMessage:
      getNewVersionBlockedMessage(loadedDapp) ||
      getStoreSubmissionPermissionMessage(loadedDapp) ||
      (wallet.publicKey?.toBase58() !== loadedDapp?.walletAddress
        ? `Connect your app wallet (${loadedDapp?.walletAddress || ''}) to continue.`
        : undefined),
    lastPublishedAt,
    missingRequiredFields,
    formKey,
    formData,
    previewUploads,
    previewPanelData,
    existingPreviewUrls: assets.dappPreviewUrls,
    resolvedDappIconUrl,
    resolvedBannerUrl,
    resolvedPreviewUrls,
    resolvedEditorsChoiceGraphicUrl,
    handleInputChange,
    handleSingleFileChange,
    handleSubmit,
    handleSaveDraft,
    handleDiscard,
    setIsFormValid,
    removeExistingDappIcon: () => updateAsset({ dappIconUrl: null }),
    removeExistingBanner: () => updateAsset({ bannerUrl: null }),
    removeExistingPreview: (index: number) =>
      setAssets((previous) => ({
        ...previous,
        dappPreviewUrls: setAt(previous.dappPreviewUrls, index, null, null),
      })),
    removeExistingGraphic: () => updateAsset({ editorsChoiceGraphicUrl: null }),
  };
}
